import getScrollbarWidth from '../../core/utils/KScrollbarWidth'
import { TweenLite } from 'gsap'
import ResizeAware from '../resize-aware'
import { addResizeListener, removeResizeListener } from './util'
import { sub, unSub, DOCUMENT_RESIZE } from '../../core/utils/KGevent'
import Bar from './Bar'
import _ from 'lodash'

/**
 * 滚动条
 */
export default {
  name: 'KScrollbar',
  props: {
    // native: Boolean,
    wrapStyle: {},
    wrapClass: {},
    viewClass: {},
    viewStyle: {},
    // 如果 container 尺寸不会发生变化，最好设置它可以优化性能
    noResize: Boolean,
    tag: {
      type: String,
      default: 'div',
    },
    hasMore: Boolean,
    loading: Boolean,
    // 只要变化，就会触发 update
    updateTrigger: {
      validator: () => true,
    },
    scrollTopOffset: {
      type: Number,
      default: 0,
    },
    scrollBottomOffset: {
      type: Number,
      default: 0,
    },
    // 是否监听滚动，会触发 scroll 事件
    detectScroll: {
      type: Boolean,
      default: false,
    },
    // 限制滚动溢出行为（bounce/refreshes..）只发生在当前滚动范围内，且不触发相邻滚动区域的滚动联动 [](https://developer.mozilla.org/de/docs/Web/CSS/overscroll-behavior)
    overScrollContain: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sizeWidth: '0',
      sizeHeight: '0',
      moveX: 0,
      moveY: 0,
      scrolling: false,
      scrollVerticalDirection: '',
      scrollHorizontalDirection: '',
    }
  },
  computed: {
    wrap() {
      return this.$refs.wrap
    },
  },
  watch: {
    updateTrigger() {
      this.update()
    },
    scrolling(value) {
      this.$emit('scrolling', value)

      if (!value) {
        this.scrollVerticalDirection = ''
        this.scrollHorizontalDirection = ''
      }
    },
    scrollVerticalDirection(value) {
      this.$emit('scrollVerticalDirection', value)
    },
    scrollHorizontalDirection(value) {
      this.$emit('scrollHorizontalDirection', value)
    },
  },
  methods: {
    /**
     * @vuese
     * 滚动到顶部
     * @arg 滚动时长（单位：毫秒）
     */
    scroll2Top(duration = 300) {
      const { wrap } = this.$refs
      TweenLite.to(wrap, duration / 1000, {
        scrollTop: 0,
      })
    },
    /**
     * @vuese
     * 滚动到底部
     * @arg 滚动时长（单位：毫秒）
     */
    scroll2Bottom(duration = 300) {
      const { wrap } = this.$refs
      TweenLite.to(wrap, duration / 1000, {
        scrollTop: wrap.scrollHeight,
      })
    },
    shouldLoadMore() {
      // 是否应该加载更多：当数据内容未撑满容器的高度时，触发
      if (this.loading && !this.hasMore) {
        return false
      }

      const { wrap } = this.$refs
      return (wrap.clientHeight + wrap.scrollTop + this.scrollBottomOffset >= wrap.scrollHeight) && wrap.scrollHeight
    },
    handleScroll() {
      // const wrap = this.wrap
      const { wrap, resize } = this.$refs
      if (!wrap || !resize) {
        return
      }

      this.detectScrolling()

      this.moveY = ((wrap.scrollTop * 100) / wrap.clientHeight)
      this.moveX = ((wrap.scrollLeft * 100) / wrap.clientWidth)
      if (!this.loading && (wrap.scrollHeight - this.scrollBottomOffset <= wrap.scrollTop + wrap.clientHeight + 2)) {
        this.$emit('scrollBottom')
      } else if (!this.loading && (wrap.scrollTop - this.scrollTopOffset <= 0)) {
        this.$emit('scrollTop')
      }
    },
    detectScrolling() {
      const { wrap } = this.$refs
      const scrollTop = wrap.scrollTop
      const scrollLeft = wrap.scrollLeft

      if (this.detectScroll) {
        this.$emit('scroll', {
          scrollTop,
          scrollLeft,
          scrollHeight: wrap.scrollHeight,
          height: wrap.clientHeight,
          width: wrap.clientWidth,
        })
      }

      // 检查滚动是否已经停止
      this.scrolling = true
      clearTimeout(this.scrollingTimer)
      this.scrollingTimer = setTimeout(() => {
        this.scrolling = false
      }, 200)

      const { previousScrollTop, previousScrollLeft } = this
      if (previousScrollTop) {
        this.scrollVerticalDirection = scrollTop - previousScrollTop > 0 ? 'bottom' : 'top'
      }
      if (previousScrollLeft) {
        this.scrollHorizontalDirection = scrollLeft - previousScrollLeft > 0 ? 'right' : 'left'
      }
      this.previousScrollTop = scrollTop
      this.previousScrollLeft = scrollLeft
    },
    update() {
      const { wrap, resize } = this.$refs
      if (!wrap || !resize) {
        return
      }

      let heightPercentage, widthPercentage
      // const wrap = this.$el // this.wrap
      // if (!wrap) return
      heightPercentage = (wrap.clientHeight * 100 / resize.scrollHeight)
      widthPercentage = (wrap.clientWidth * 100 / resize.scrollWidth)

      this.sizeHeight = (heightPercentage < 100) ? (heightPercentage + '%') : ''
      this.sizeWidth = (widthPercentage < 100) ? (widthPercentage + '%') : ''

      if (this.detectScroll) {
        this.handleScroll()
      }
    },

    // api:
    scrollToBottom() {
      this.$refs.wrap.scrollTop = this.$refs.wrap.scrollHeight
    },
    // api:
    setScrollLeft(value) {
      console.log('setScrollLeft: ', value)
      this.$refs.wrap.scrollLeft = value
    },
    // api:
    setScrollTop(value) {
      this.$refs.wrap.scrollTop = value
    },
  },
  mounted() {
    this.$refs.wrap.style.overflow = 'scroll' // 避免ssr渲染时显示浏览器默认滚动条，改为scrollbar组件加载后内容区域可滚动
    this.$nextTick(this.update)
    if (!this.noResize) {
      addResizeListener(this.$refs.resize, this.update)
      sub(DOCUMENT_RESIZE, this.update)
    }
    this.$emit('mounted')
  },
  beforeDestroy() {
    if (!this.noResize) {
      removeResizeListener(this.$refs.resize, this.update)
      unSub(DOCUMENT_RESIZE, this.update)
    }
  },
  render() {
    let gutter = getScrollbarWidth()
    let style = this.wrapStyle

    if (gutter) {
      const gutterWidth = `-${gutter}px`
      const overScrollContain = this.overScrollContain ? { 'overscroll-behavior': 'contain' } : {}
      const gutterStyle = {
        'margin-bottom': gutterWidth,
        'margin-right': gutterWidth,
        'height': `calc(100% + ${gutter}px)`,
        'width': `calc(100% + ${gutter}px)`,
        ...overScrollContain,
      }
      style = _.assign(style || {}, gutterStyle)
    }

    const css = { 'km-scrollbar__view': true }
    this.viewClass && (css[this.viewClass] = true)
    const viewData = {
      class: css,
      style: this.viewStyle,
      props: {},
      ref: 'resize',
    }
    const view = (
      <div {...viewData}>
        {this.$slots.default}
      </div>
    )
    const wrap = (
      <div
        ref="wrap"
        style={style}
        onScroll={this.handleScroll}
        class={[this.wrapClass, 'km-scrollbar__wrap']}
      >
        {[view]}
        <ResizeAware
          onResize={() => {
            this.update()
          }}
        />
      </div>
    )

    const nodes = [
      wrap,
      gutter ? <Bar
        move={this.moveX}
        size={this.sizeWidth}
      ></Bar> : <div/>,
      gutter ? <Bar
        vertical
        move={this.moveY}
        size={this.sizeHeight}
      ></Bar> : <div/>,
    ]

    return <div class="km-scrollbar">{nodes}</div>
  },
}
